import { useCallback, useState, useEffect } from 'react'
import { loadGoogleApiScripts, initializePickerApi } from 'api/services/GoogleApi'
import { REACT_APP_GOOGLE_CLIENT_ID } from 'env'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { GoogleDriveClient } from 'api/clients/GoogleDriveClient'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

export interface PickerDocument {
  id: string
  name: string
  mimeType: string
  url?: string
}

export interface PickerSelection {
  files: PickerDocument[]
  folders: PickerDocument[]
}

interface UseGoogleDrivePickerReturn {
  openPicker: (onSelect: (selection: PickerSelection) => void) => void
  isLoading: boolean
  isConnected: boolean
}

const FOLDER_MIME_TYPE = 'application/vnd.google-apps.folder'

const ALLOWED_MIME_TYPES = [
  FOLDER_MIME_TYPE,
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/svg+xml',
  'video/mp4',
  'video/mpeg',
  'video/quicktime',
  'video/webm',
  'application/pdf',
  'text/plain',
  'text/markdown',
].join(',')

export const useGoogleDrivePicker = (): UseGoogleDrivePickerReturn => {
  const [isReady, setIsReady] = useState(false)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [tokenExpiresAt, setTokenExpiresAt] = useState<number | null>(null)
  const [isTokenLoading, setIsTokenLoading] = useState(false)
  const workspaceId = useCurrentWorkspaceId()

  const { data: integrationsData } = useExternalIntegrations()
  const googleDriveIntegration = integrationsData?.external_integrations?.find(
    (integration) => integration.provider === ExternalIntegrationEnum.GoogleDrive,
  )

  useEffect(() => {
    if (!googleDriveIntegration || !workspaceId) {
      setAccessToken(null)
      setTokenExpiresAt(null)
      setIsReady(false)
      return
    }

    const initialize = async () => {
      await loadGoogleApiScripts()
      await initializePickerApi()
      setIsReady(true)

      const response = await GoogleDriveClient.getAccessToken({
        workspaceId,
        integrationId: googleDriveIntegration.id,
      })
      if (response.type === 'success') {
        setAccessToken(response.data.access_token)
        setTokenExpiresAt(Date.now() + response.data.expires_in * 1000 - 60_000)
      }
    }

    initialize()
  }, [googleDriveIntegration, workspaceId])

  const openPicker = useCallback(
    async (onSelect: (selection: PickerSelection) => void) => {
      if (!window.google?.picker || !googleDriveIntegration || !workspaceId) return

      let tokenToUse = accessToken
      const tokenExpired = tokenExpiresAt !== null && Date.now() >= tokenExpiresAt
      if (!tokenToUse || tokenExpired) {
        setIsTokenLoading(true)
        const response = await GoogleDriveClient.getAccessToken({
          workspaceId,
          integrationId: googleDriveIntegration.id,
        })
        setIsTokenLoading(false)
        if (response.type !== 'success') return
        tokenToUse = response.data.access_token
        setAccessToken(tokenToUse)
        setTokenExpiresAt(Date.now() + response.data.expires_in * 1000 - 60_000)
      }

      if (!tokenToUse) return

      const docsView = new window.google.picker.DocsView()
        .setMode(window.google.picker.DocsViewMode.LIST)
        .setIncludeFolders(true)
        .setOwnedByMe(true)
        .setSelectFolderEnabled(true)
        .setMimeTypes(ALLOWED_MIME_TYPES)

      const picker = new window.google.picker.PickerBuilder()
        .addView(docsView)
        .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
        .setMaxItems(100)
        .setOAuthToken(tokenToUse)
        .setAppId(REACT_APP_GOOGLE_CLIENT_ID.split('-')[0])
        .setCallback((data: google.picker.ResponseObject) => {
          if (data.action === window.google.picker.Action.PICKED) {
            const documents = (data.docs || []) as PickerDocument[]
            onSelect({
              files: documents.filter((doc) => doc.mimeType !== FOLDER_MIME_TYPE),
              folders: documents.filter((doc) => doc.mimeType === FOLDER_MIME_TYPE),
            })
          }
        })
        .build()

      picker.setVisible(true)
    },
    [accessToken, googleDriveIntegration, tokenExpiresAt, workspaceId],
  )

  return {
    openPicker,
    isLoading: !isReady || isTokenLoading,
    isConnected: Boolean(googleDriveIntegration),
  }
}
